/**
 * WP2.5 funnel analytics — moderator-only overview.
 *
 * Numbers come straight from `GET /api/v1/moderators/analytics/overview/`
 * (see `apps/analytics/services.py` in the backend for metric definitions).
 * This page renders funnel steps, D1-7 tiered success, cycle times, the
 * moderator-hours histogram and the manual-deals block, with filters for
 * `from` / `to` / `corridor` / `source`.
 */

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ModeratorLayout from "@/components/ModeratorLayout/ModeratorLayout";
import { EMPTY_VALUE, formatNumber } from "@/i18n/format";
import type { AnalyticsOverview, PerModeratorHours } from "@/services/moderators/generated/model";
import { useModeratorsAnalyticsOverviewRetrieve } from "@/services/moderators/generated/moderators-analytics/moderators-analytics";
import "./ModeratorAnalyticsPage.css";

type SourceKey = "institution" | "event" | "partner" | "other" | "unknown";

const SOURCE_OPTIONS: readonly SourceKey[] = ["institution", "event", "partner", "other", "unknown"] as const;

// Pilot default: Quebec → GCC (D1-1). Add more as settings.MATCHING_CORRIDORS grows.
const CORRIDOR_OPTIONS = [{ value: "quebec_gcc", label: "Quebec → GCC" }];

const todayISO = () => new Date().toISOString().slice(0, 10);
const minusDaysISO = (days: number) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
};

const fmtPct = (value: number | null | undefined): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return EMPTY_VALUE;
  return `${formatNumber(value, { maximumFractionDigits: 1 })}%`;
};

const fmtNum = (value: number | null | undefined, digits = 0): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return EMPTY_VALUE;
  return formatNumber(value, { maximumFractionDigits: digits });
};

const ModeratorAnalyticsPage = () => <ModeratorLayout>{() => <AnalyticsContent />}</ModeratorLayout>;

const AnalyticsContent = () => {
  const { t } = useTranslation(["moderator", "common"]);
  const [from, setFrom] = useState(() => minusDaysISO(90));
  const [to, setTo] = useState(() => todayISO());
  const [corridor, setCorridor] = useState("");
  const [source, setSource] = useState("");
  const [applied, setApplied] = useState({ from, to, corridor, source });

  const params = useMemo(() => {
    const p: Record<string, string> = {};
    if (applied.from) p.from = applied.from;
    if (applied.to) p.to = applied.to;
    if (applied.corridor) p.corridor = applied.corridor;
    if (applied.source) p.source = applied.source;
    return p;
  }, [applied]);

  const { data, isLoading, error, refetch, isRefetching } = useModeratorsAnalyticsOverviewRetrieve(params as never);

  const overview = data as AnalyticsOverview | undefined;

  return (
    <div className="moderator-analytics">
      <header className="moderator-analytics__header">
        <h1>{t("analytics.title")}</h1>
        <p>{t("analytics.subtitle")}</p>
      </header>

      <section className="moderator-analytics__filters" aria-label={t("analytics.filters.apply")}>
        <label>
          <span>{t("analytics.filters.from")}</span>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label>
          <span>{t("analytics.filters.to")}</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        <label>
          <span>{t("analytics.filters.corridor")}</span>
          <select value={corridor} onChange={(e) => setCorridor(e.target.value)}>
            <option value="">{t("analytics.filters.allCorridors")}</option>
            {CORRIDOR_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>{t("analytics.filters.source")}</span>
          <select value={source} onChange={(e) => setSource(e.target.value)}>
            <option value="">{t("analytics.filters.allSources")}</option>
            {SOURCE_OPTIONS.map((o) => (
              <option key={o} value={o}>
                {sourceLabel(t, o)}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="moderator-analytics__apply"
          onClick={() => {
            setApplied({ from, to, corridor, source });
            refetch();
          }}
          disabled={isRefetching}
        >
          {t("analytics.filters.apply")}
        </button>
      </section>

      {isLoading ? (
        <p>{t("common:states.loading")}</p>
      ) : error ? (
        <p className="moderator-analytics__error">{t("analytics.loadError")}</p>
      ) : overview ? (
        <>
          <FunnelCard overview={overview} />
          <TieredCard overview={overview} />
          <CycleCard overview={overview} />
          <HoursCard overview={overview} />
          <ManualCard overview={overview} />
        </>
      ) : null}
    </div>
  );
};

const sourceLabel = (t: ReturnType<typeof useTranslation<"moderator">>["t"], key: SourceKey): string => {
  switch (key) {
    case "institution":
      return t("analytics.sources.institution");
    case "event":
      return t("analytics.sources.event");
    case "partner":
      return t("analytics.sources.partner");
    case "other":
      return t("analytics.sources.other");
    case "unknown":
      return t("analytics.sources.unknown");
  }
};

const bucketLabel = (
  t: ReturnType<typeof useTranslation<"moderator">>["t"],
  key: "le30" | "le60" | "le120" | "gt120",
): string => {
  switch (key) {
    case "le30":
      return t("analytics.moderatorHours.bucketLe30");
    case "le60":
      return t("analytics.moderatorHours.bucketLe60");
    case "le120":
      return t("analytics.moderatorHours.bucketLe120");
    case "gt120":
      return t("analytics.moderatorHours.bucketGt120");
  }
};

const funnelLabel = (
  t: ReturnType<typeof useTranslation<"moderator">>["t"],
  key: "matched" | "accepted" | "inProgress" | "won",
): string => {
  switch (key) {
    case "matched":
      return t("analytics.funnel.matched");
    case "accepted":
      return t("analytics.funnel.accepted");
    case "inProgress":
      return t("analytics.funnel.inProgress");
    case "won":
      return t("analytics.funnel.won");
  }
};

const FunnelCard = ({ overview }: { overview: AnalyticsOverview }) => {
  const { t } = useTranslation("moderator");
  const f = overview.funnel;
  const rows: Array<{
    labelKey: "matched" | "accepted" | "inProgress" | "won";
    count: number;
    fromMatched: number | null;
    fromPrev: number | null;
  }> = [
    { labelKey: "matched", count: f.matched.count, fromMatched: f.matched.fromMatched, fromPrev: null },
    {
      labelKey: "accepted",
      count: f.accepted.count,
      fromMatched: f.accepted.fromMatched,
      fromPrev: f.accepted.fromPrev,
    },
    {
      labelKey: "inProgress",
      count: f.inProgress.count,
      fromMatched: f.inProgress.fromMatched,
      fromPrev: f.inProgress.fromPrev,
    },
    { labelKey: "won", count: f.won.count, fromMatched: f.won.fromMatched, fromPrev: f.won.fromPrev },
  ];
  const empty = f.matched.count === 0;
  return (
    <section className="moderator-analytics__card">
      <header>
        <h2>{t("analytics.funnel.title")}</h2>
        <p>{t("analytics.funnel.subtitle")}</p>
      </header>
      {empty ? (
        <p>{t("analytics.funnel.noData")}</p>
      ) : (
        <>
          <ol className="moderator-analytics__funnel">
            {rows.map((r) => (
              <li key={r.labelKey}>
                <span className="moderator-analytics__funnel-label">{funnelLabel(t, r.labelKey)}</span>
                <span className="moderator-analytics__funnel-count">{fmtNum(r.count)}</span>
                <span className="moderator-analytics__funnel-pct">
                  {t("analytics.funnel.fromMatched", { value: fmtPct(r.fromMatched).replace("%", "") })}
                  {r.fromPrev !== null ? (
                    <>
                      {" · "}
                      {t("analytics.funnel.fromPrev", { value: fmtPct(r.fromPrev).replace("%", "") })}
                    </>
                  ) : null}
                </span>
              </li>
            ))}
          </ol>
          <div className="moderator-analytics__exits">
            <h3>{t("analytics.funnel.exitsTitle")}</h3>
            <dl>
              <div>
                <dt>{t("analytics.funnel.exitsDeclined")}</dt>
                <dd>{fmtNum(f.exits.declined)}</dd>
              </div>
              <div>
                <dt>{t("analytics.funnel.exitsLost")}</dt>
                <dd>{fmtNum(f.exits.lost)}</dd>
              </div>
            </dl>
          </div>
        </>
      )}
    </section>
  );
};

const TieredCard = ({ overview }: { overview: AnalyticsOverview }) => {
  const { t } = useTranslation("moderator");
  const { early, main } = overview.tieredSuccess;
  return (
    <section className="moderator-analytics__card moderator-analytics__tiered">
      <header>
        <h2>{t("analytics.tiered.title")}</h2>
        <p>{t("analytics.tiered.cohortNote")}</p>
      </header>
      <div className="moderator-analytics__tier-grid">
        <TierBlock
          label={t("analytics.tiered.earlyLabel", { days: early.windowDays })}
          numerator={early.numerator}
          denominator={early.denominator}
          rate={early.rate}
        />
        <TierBlock
          label={t("analytics.tiered.mainLabel", { days: main.windowDays })}
          numerator={main.numerator}
          denominator={main.denominator}
          rate={main.rate}
        />
      </div>
    </section>
  );
};

const TierBlock = ({
  label,
  numerator,
  denominator,
  rate,
}: {
  label: string;
  numerator: number;
  denominator: number;
  rate: number | null;
}) => {
  const { t } = useTranslation("moderator");
  return (
    <div className="moderator-analytics__tier">
      <p className="moderator-analytics__tier-label">{label}</p>
      <p className="moderator-analytics__tier-rate">{fmtPct(rate)}</p>
      <p className="moderator-analytics__tier-sub">
        {denominator === 0
          ? t("analytics.tiered.noCohort")
          : t("analytics.tiered.numerator", { num: numerator, denom: denominator })}
      </p>
    </div>
  );
};

const CycleCard = ({ overview }: { overview: AnalyticsOverview }) => {
  const { t } = useTranslation("moderator");
  const parts: Array<{
    labelKey: "matchToAccept" | "acceptToFirstMeeting" | "acceptToWon";
    data: typeof overview.cycleTimes.matchToAccept;
  }> = [
    { labelKey: "matchToAccept", data: overview.cycleTimes.matchToAccept },
    { labelKey: "acceptToFirstMeeting", data: overview.cycleTimes.acceptToFirstMeeting },
    { labelKey: "acceptToWon", data: overview.cycleTimes.acceptToWon },
  ];
  const maxP75 = Math.max(1, ...parts.map((p) => p.data.p75Days ?? 0));
  return (
    <section className="moderator-analytics__card">
      <header>
        <h2>{t("analytics.cycleTimes.title")}</h2>
      </header>
      <table className="moderator-analytics__cycle">
        <thead>
          <tr>
            <th>&nbsp;</th>
            <th>{t("analytics.cycleTimes.median")}</th>
            <th>{t("analytics.cycleTimes.p75")}</th>
            <th aria-hidden="true" />
          </tr>
        </thead>
        <tbody>
          {parts.map((p) => {
            const label =
              p.labelKey === "matchToAccept"
                ? t("analytics.cycleTimes.matchToAccept")
                : p.labelKey === "acceptToFirstMeeting"
                  ? t("analytics.cycleTimes.acceptToFirstMeeting")
                  : t("analytics.cycleTimes.acceptToWon");
            return (
              <tr key={p.labelKey}>
                <td>
                  {label}
                  <br />
                  <small>{t("analytics.cycleTimes.count", { n: p.data.count })}</small>
                </td>
                <td>{fmtNum(p.data.medianDays, 1)}</td>
                <td>{fmtNum(p.data.p75Days, 1)}</td>
                <td className="moderator-analytics__cycle-bar">
                  <span
                    style={{
                      width: `${Math.min(100, ((p.data.p75Days ?? 0) / maxP75) * 100)}%`,
                    }}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
};

const HoursCard = ({ overview }: { overview: AnalyticsOverview }) => {
  const { t } = useTranslation("moderator");
  const h = overview.moderatorHours;
  const bucketMax = Math.max(1, h.buckets.le30, h.buckets.le60, h.buckets.le120, h.buckets.gt120);
  const bucketRows: Array<{ key: "le30" | "le60" | "le120" | "gt120"; value: number; variant: string }> = [
    { key: "le30", value: h.buckets.le30, variant: "ok" },
    { key: "le60", value: h.buckets.le60, variant: "ok" },
    { key: "le120", value: h.buckets.le120, variant: "warn" },
    { key: "gt120", value: h.buckets.gt120, variant: "bad" },
  ];
  return (
    <section className="moderator-analytics__card">
      <header>
        <h2>{t("analytics.moderatorHours.title")}</h2>
        <p>{t("analytics.moderatorHours.subtitle", { cap: h.capMinutes })}</p>
      </header>
      {h.deals === 0 ? (
        <p>{t("analytics.moderatorHours.empty")}</p>
      ) : (
        <>
          <dl className="moderator-analytics__kpis">
            <div>
              <dt>{t("analytics.moderatorHours.deals")}</dt>
              <dd>{fmtNum(h.deals)}</dd>
            </div>
            <div>
              <dt>{t("analytics.moderatorHours.total")}</dt>
              <dd>{fmtNum(h.totalMinutes)}</dd>
            </div>
            <div>
              <dt>{t("analytics.moderatorHours.avg")}</dt>
              <dd>{fmtNum(h.avgMinutesPerDeal, 1)}</dd>
            </div>
            <div>
              <dt>{t("analytics.moderatorHours.median")}</dt>
              <dd>{fmtNum(h.medianMinutesPerDeal, 1)}</dd>
            </div>
          </dl>

          <h3>{t("analytics.moderatorHours.bucketsTitle")}</h3>
          <ul className="moderator-analytics__buckets">
            {bucketRows.map((b) => (
              <li key={b.key}>
                <span className="moderator-analytics__bucket-label">{bucketLabel(t, b.key)}</span>
                <span className="moderator-analytics__bucket-bar">
                  <span data-variant={b.variant} style={{ width: `${(b.value / bucketMax) * 100}%` }} />
                </span>
                <span className="moderator-analytics__bucket-value">{fmtNum(b.value)}</span>
              </li>
            ))}
          </ul>

          <h3>{t("analytics.moderatorHours.perModeratorTitle")}</h3>
          <table className="moderator-analytics__per-mod">
            <thead>
              <tr>
                <th>{t("analytics.moderatorHours.name")}</th>
                <th>{t("analytics.moderatorHours.minutes")}</th>
                <th>{t("analytics.moderatorHours.dealsCount")}</th>
              </tr>
            </thead>
            <tbody>
              {h.perModerator.map((row: PerModeratorHours) => (
                <tr key={`${row.moderatorId ?? "removed"}-${row.name}`}>
                  <td>{row.name || t("analytics.moderatorHours.removedModerator")}</td>
                  <td>{fmtNum(row.minutes)}</td>
                  <td>{fmtNum(row.deals)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
};

const ManualCard = ({ overview }: { overview: AnalyticsOverview }) => {
  const { t } = useTranslation("moderator");
  const m = overview.manualDeals;
  return (
    <section className="moderator-analytics__card">
      <header>
        <h2>{t("analytics.manualDeals.title")}</h2>
        <p>{t("analytics.manualDeals.subtitle")}</p>
      </header>
      <dl className="moderator-analytics__kpis">
        <div>
          <dt>{t("analytics.manualDeals.count")}</dt>
          <dd>{fmtNum(m.count)}</dd>
        </div>
        <div>
          <dt>{t("analytics.manualDeals.won")}</dt>
          <dd>{fmtNum(m.won)}</dd>
        </div>
        <div>
          <dt>{t("analytics.manualDeals.lost")}</dt>
          <dd>{fmtNum(m.lost)}</dd>
        </div>
        <div>
          <dt>{t("analytics.manualDeals.open")}</dt>
          <dd>{fmtNum(m.open)}</dd>
        </div>
      </dl>
    </section>
  );
};

export default ModeratorAnalyticsPage;
